import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { ISmartrrBundleConfig } from "@smartrr/shared/entities/SellingPlanGroup";
import { getBundleByBundleId } from "@smartrr/shared/utils/bundles";
import { useEffect, useMemo, useState } from "react";

import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

export const useAllBundles = () => {
  const allBundles = useSmartrrCustomerSelector(state => state.bundles.bundle);
  const areBundlesLoading = useSmartrrCustomerSelector(state => state.bundles.isLoading);
  const allCollections = useSmartrrCustomerSelector(state => state.bundles.bundleCollections);

  const allBundleProductIds = useMemo(() => {
    const ids: string[] = [];
    if (allCollections.length) {
      for (const collection of allCollections) {
        if (collection.products?.edges.length) {
          for (const edge of collection.products.edges) {
            const productId = edge.node.id;
            if (!ids.includes(productId)) {
              ids.push(productId);
            }

            continue;
          }
        }
      }
    }

    return ids;
  }, [allCollections]);

  const hideAllBundleItems = allBundles.some(bundle => bundle.hideBundleItems) ?? false;

  return useMemo(() => {
    return {
      allBundles,
      areBundlesLoading,
      allCollections,
      allBundleProductIds,
      hideAllBundleItems,
    };
  }, [areBundlesLoading, allBundles, allCollections, allBundleProductIds, hideAllBundleItems]);
};

export const useCurrentBundle = (delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState> | undefined) => {
  const { allBundles, areBundlesLoading, allCollections, allBundleProductIds, hideAllBundleItems } =
    useAllBundles();
  const [bundle, setBundle] = useState<ISmartrrBundleConfig | undefined>(
    delivery?.purchaseState ? getBundleByBundleId(allBundles, delivery?.purchaseState!) : undefined
  );

  useEffect(() => {
    if (!delivery) {
      return;
    }

    if (delivery && !areBundlesLoading && allBundles) {
      setBundle(getBundleByBundleId(allBundles, delivery?.purchaseState!)!);
    }
  }, [allBundles, areBundlesLoading, delivery]);

  return useMemo(() => {
    return {
      bundle,
      allBundles,
      allCollections,
      allBundleProductIds,
      hideAllBundleItems,
    };
  }, [delivery, areBundlesLoading, allBundles, bundle, allCollections, allBundleProductIds, hideAllBundleItems]);
};
